<script setup>
import { computed } from 'vue';
import { formatNumber } from '@chatwoot/utils';
import { useI18n } from 'vue-i18n';

import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  pageTitle: { type: String, required: true },
  hasAppliedFilters: { type: Boolean, required: true },
  hasActiveFolders: { type: Boolean, required: true },
  activeStatus: { type: String, required: true },
  conversationStats: { type: Object, required: true },
  isListLoading: { type: Boolean, required: true },
  searchQuery: { type: String, default: '' },
  // Estado dos menus do cromo, que vivem no QuickFilterTabs mas são
  // DISPARADOS daqui (o header V3 é quem tem os ícones).
  filterActive: { type: Boolean, default: false },
  filterOpen: { type: Boolean, default: false },
  shortcutsOpen: { type: Boolean, default: false },
  isListCompact: { type: Boolean, default: false },
  isFocusMode: { type: Boolean, default: false },
});

const emit = defineEmits([
  'deleteFolders',
  'resetFilters',
  'filtersModal',
  'update:searchQuery',
  'toggleFilter',
  'toggleShortcuts',
  'toggleCompact',
]);

// Manda o rect do botão junto: quem desenha o painel é o QuickFilterTabs, e
// sem a âncora ele não sabe onde se posicionar.
const onToggle = (which, event) => {
  const rect = event.currentTarget?.getBoundingClientRect?.() || null;
  if (which === 'filter') emit('toggleFilter', rect);
  else emit('toggleShortcuts', rect);
};

const { t } = useI18n();

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return props.hasAppliedFilters || props.hasActiveFolders;
});

// SiteUp: label do status no header. Status combinado (ex.: "open,snoozed",
// dos filtros configuráveis) não tem chave i18n própria — traduz cada status
// atômico e junta (ex.: "Aberto, Adiado").
const statusLabel = computed(() => {
  const status = props.activeStatus || '';
  if (status.includes(',')) {
    return (
      status
        .split(',')
        // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
        .map(s => t(`CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.${s}.TEXT`))
        .join(', ')
    );
  }
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  return t(`CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.${status}.TEXT`);
});

const allCount = computed(() => props.conversationStats?.allCount || 0);
const formattedAllCount = computed(() => formatNumber(allCount.value));
</script>

<template>
  <div
    class="md:border-b md:border-n-strong"
    :class="{
      'border-b border-n-strong': hasAppliedFiltersOrActiveFolders,
    }"
  >
    <!-- Compacto: busca + cromo numa fileira. Título some — a lista já se
         identifica. Busca fica digitável, sem clique extra. -->
    <div v-if="isListCompact" class="flex items-center gap-0.5 px-2 py-1.5">
      <div class="relative min-w-0 flex-1">
        <span
          class="pointer-events-none absolute inset-y-0 flex items-center text-n-slate-10 ltr:left-2.5 rtl:right-2.5"
        >
          <span class="i-ri-search-line size-3.5" />
        </span>
        <input
          data-testid="conversation-list-search-input"
          type="search"
          :value="searchQuery"
          :aria-label="$t('CHAT_LIST.SEARCH.INPUT')"
          :placeholder="$t('CHAT_LIST.SEARCH.INPUT_SHORT')"
          class="reset-base !mb-0 min-w-0 h-8 w-full appearance-none rounded-full border-0 bg-n-alpha-2 py-1.5 text-[13px] text-n-slate-12 outline-none placeholder:text-n-slate-10 focus:bg-n-solid-2 focus:ring-1 focus:ring-n-brand ltr:pl-8 ltr:pr-7 rtl:pr-8 rtl:pl-7 [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden"
          @input="emit('update:searchQuery', $event.target.value)"
        />
        <button
          v-if="searchQuery"
          data-testid="conversation-list-search-clear"
          type="button"
          class="absolute inset-y-0 flex items-center px-1 text-n-slate-9 hover:text-n-slate-12 ltr:right-1.5 rtl:left-1.5"
          :aria-label="$t('FILTER.CLEAR_BUTTON_LABEL')"
          @click="emit('update:searchQuery', '')"
        >
          <span class="i-ri-close-line size-3.5" />
        </button>
      </div>
      <button
        v-tooltip.bottom="
          isFocusMode
            ? $t('CHAT_LIST.WORKBENCH.HIDE')
            : $t('CHAT_LIST.WORKBENCH.EXPAND')
        "
        type="button"
        data-testid="chat-list-workbench-toggle"
        class="inline-flex size-8 shrink-0 items-center justify-center rounded-full text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12"
        :aria-pressed="true"
        :aria-label="
          isFocusMode
            ? $t('CHAT_LIST.WORKBENCH.HIDE')
            : $t('CHAT_LIST.WORKBENCH.EXPAND')
        "
        @click="emit('toggleCompact')"
      >
        <span
          class="size-4"
          :class="
            isFocusMode ? 'i-ri-sidebar-fold-line' : 'i-ri-sidebar-unfold-line'
          "
        />
      </button>
      <button
        v-tooltip.bottom="$t('CHAT_LIST.QUICK_FILTERS.FILTER')"
        type="button"
        data-testid="chat-list-filter"
        class="relative inline-flex size-8 shrink-0 items-center justify-center rounded-full transition-colors"
        :class="
          filterActive || filterOpen
            ? 'bg-n-brand/10 text-n-blue-11'
            : 'text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12'
        "
        :aria-expanded="filterOpen"
        aria-haspopup="dialog"
        :aria-label="$t('CHAT_LIST.QUICK_FILTERS.FILTER')"
        @click="onToggle('filter', $event)"
      >
        <span class="i-ri-filter-3-line size-4" />
        <span
          v-if="filterActive"
          data-testid="chat-list-filter-dot"
          class="absolute right-1 top-1 size-1.5 rounded-full bg-n-blue-9"
          aria-hidden="true"
        />
      </button>
      <button
        v-tooltip.bottom="$t('CHAT_LIST.QUICK_FILTERS.SHORTCUTS')"
        type="button"
        data-testid="chat-list-shortcuts"
        class="inline-flex size-8 shrink-0 items-center justify-center rounded-full transition-colors"
        :class="
          shortcutsOpen
            ? 'bg-n-brand/10 text-n-blue-11'
            : 'text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12'
        "
        :aria-expanded="shortcutsOpen"
        aria-haspopup="dialog"
        :aria-label="$t('CHAT_LIST.QUICK_FILTERS.SHORTCUTS')"
        @click="onToggle('shortcuts', $event)"
      >
        <span class="i-ri-add-line size-4" />
      </button>
    </div>

    <!-- Confortável: título + ações, busca numa segunda linha. -->
    <template v-else>
      <div
        class="flex min-h-[3.25rem] flex-wrap items-center justify-between gap-2 px-3 py-2"
      >
        <div class="flex min-w-0 basis-36 flex-1 items-center">
          <h1
            class="truncate text-xl font-extrabold tracking-tight text-n-slate-12"
            :title="pageTitle"
          >
            {{ pageTitle }}
          </h1>
          <span
            v-if="
              allCount > 0 && hasAppliedFiltersOrActiveFolders && !isListLoading
            "
            class="px-2 py-1 my-0.5 mx-1 rounded-md capitalize bg-n-slate-3 text-xxs text-n-slate-12 shrink-0"
            :title="allCount"
          >
            {{ formattedAllCount }}
          </span>
          <span
            v-if="!hasAppliedFiltersOrActiveFolders"
            class="mx-1 my-0.5 shrink-0 rounded-md bg-n-slate-3 px-2 py-1 text-xxs capitalize text-n-slate-12"
          >
            {{ statusLabel }}
          </span>
        </div>
        <div
          class="ml-auto flex max-w-full shrink-0 flex-wrap items-center justify-end gap-1"
        >
          <button
            v-tooltip.bottom="$t('CHAT_LIST.WORKBENCH.COLLAPSE')"
            type="button"
            data-testid="chat-list-workbench-toggle"
            class="inline-flex size-9 items-center justify-center rounded-full text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12"
            :aria-pressed="false"
            :aria-label="$t('CHAT_LIST.WORKBENCH.COLLAPSE')"
            @click="emit('toggleCompact')"
          >
            <span class="i-ri-sidebar-fold-line size-[18px]" />
          </button>
          <button
            v-tooltip.bottom="$t('CHAT_LIST.QUICK_FILTERS.FILTER')"
            type="button"
            data-testid="chat-list-filter"
            class="relative inline-flex size-9 items-center justify-center rounded-full transition-colors"
            :class="
              filterActive || filterOpen
                ? 'bg-n-brand/10 text-n-blue-11'
                : 'text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12'
            "
            :aria-expanded="filterOpen"
            aria-haspopup="dialog"
            :aria-label="$t('CHAT_LIST.QUICK_FILTERS.FILTER')"
            @click="onToggle('filter', $event)"
          >
            <span class="i-ri-filter-3-line size-[18px]" />
            <span
              v-if="filterActive"
              data-testid="chat-list-filter-dot"
              class="absolute right-1.5 top-1.5 size-1.5 rounded-full bg-n-blue-9"
              aria-hidden="true"
            />
          </button>
          <button
            v-tooltip.bottom="$t('CHAT_LIST.QUICK_FILTERS.SHORTCUTS')"
            type="button"
            data-testid="chat-list-shortcuts"
            class="inline-flex size-9 items-center justify-center rounded-full transition-colors"
            :class="
              shortcutsOpen
                ? 'bg-n-brand/10 text-n-blue-11'
                : 'text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12'
            "
            :aria-expanded="shortcutsOpen"
            aria-haspopup="dialog"
            :aria-label="$t('CHAT_LIST.QUICK_FILTERS.SHORTCUTS')"
            @click="onToggle('shortcuts', $event)"
          >
            <span class="i-ri-add-line size-[18px]" />
          </button>
          <template v-if="hasAppliedFilters && !hasActiveFolders">
            <NextButton
              v-tooltip.top-end="$t('FILTER.CLEAR_BUTTON_LABEL')"
              icon="i-ri-close-circle-line"
              ruby
              faded
              xs
              @click="emit('resetFilters')"
            />
          </template>
          <template v-if="hasActiveFolders">
            <div>
              <NextButton
                id="toggleConversationFilterButton"
                v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.EDIT.EDIT_BUTTON')"
                icon="i-ri-edit-2-line"
                slate
                xs
                faded
                @click="emit('filtersModal')"
              />
            </div>
            <NextButton
              v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.DELETE.DELETE_BUTTON')"
              icon="i-ri-delete-bin-line"
              ruby
              xs
              faded
              @click="emit('deleteFolders')"
            />
          </template>
        </div>
      </div>

      <div class="min-w-0 px-4 pb-2.5">
        <div class="relative min-w-0 w-full">
          <span
            class="pointer-events-none absolute inset-y-0 flex items-center text-n-slate-10 ltr:left-3.5 rtl:right-3.5"
          >
            <span class="i-ri-search-line size-4" />
          </span>
          <input
            data-testid="conversation-list-search-input"
            type="search"
            :value="searchQuery"
            :aria-label="$t('CHAT_LIST.SEARCH.INPUT')"
            :placeholder="$t('CHAT_LIST.SEARCH.INPUT')"
            class="reset-base !mb-0 min-w-0 h-[38px] w-full appearance-none rounded-full border-0 bg-n-alpha-2 py-2 text-sm text-n-slate-12 outline-none placeholder:text-n-slate-10 focus:bg-n-solid-2 focus:ring-1 focus:ring-n-brand ltr:pl-10 ltr:pr-9 rtl:pr-10 rtl:pl-9 [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden"
            @input="emit('update:searchQuery', $event.target.value)"
          />
          <button
            v-if="searchQuery"
            data-testid="conversation-list-search-clear"
            type="button"
            class="absolute inset-y-0 flex items-center px-1 text-n-slate-9 hover:text-n-slate-12 ltr:right-2.5 rtl:left-2.5"
            :aria-label="$t('FILTER.CLEAR_BUTTON_LABEL')"
            @click="emit('update:searchQuery', '')"
          >
            <span class="i-ri-close-line size-4" />
          </button>
        </div>
      </div>
    </template>
  </div>
</template>
